import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTasksPolling } from 'shared/hooks/useTasksPolling'
import { fetchPrioritySummary } from 'api/dashboardSummary'
import { fetchUsers } from 'api/messages'
import { prioritySummaryStore } from 'shared/stores/prioritySummaryStore'
import { notificationPreferencesStore } from 'shared/stores/notificationPreferencesStore'

export default function useDashboardLayout() {
	const { pathname } = useLocation()

	const isApplicationDetailsPage = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean)
		return segments[0] === 'applications' && segments.length > 1
	}, [pathname])

	const isStudentProfilePage = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean)
		return segments[0] === 'student_profile' && segments.length > 1
	}, [pathname])
	const isMessagesPage = useMemo(() => {
		const segments = pathname.split('/').filter(Boolean)
		return segments[0] === 'messages'
	}, [pathname])
	const [currentPage, setCurrentPage] = useState('')
	const [headerActions, setHeaderActionsState] = useState(null)
	const [headerLeading, setHeaderLeadingState] = useState(null)

	const pollGlobalNotifications = useCallback(async () => {
		try {
			const res = await fetchPrioritySummary()
			const counts = { ...(res?.data || {}) }
			prioritySummaryStore.setCounts(counts)

			try {
				const students = await fetchUsers()
				const unreadTotal = (students || []).reduce(
					(sum, student) => sum + (Number(student.unreadCount) || 0),
					0,
				)
				prioritySummaryStore.setMessagesUnreadTotal(unreadTotal)
			} catch (messagesError) {
				console.error('Failed to poll message unread counts:', messagesError)
			}

			return Object.entries(counts).map(([id, count]) => ({
				id,
				student_status: count,
			}))
		} catch (e) {
			prioritySummaryStore.setLoadStatus('error')
			return []
		}
	}, [])

	useTasksPolling({
		onUpdate: pollGlobalNotifications,
		enabled: true,
		initialInterval: 30000,
		maxInterval: 90000,
		backoffMultiplier: 1.5,
	})

	useEffect(() => {
		notificationPreferencesStore.ensureLoaded().catch(() => {})
	}, [])

	useEffect(() => {
		prioritySummaryStore.setRefreshFn(pollGlobalNotifications)
		return () => prioritySummaryStore.setRefreshFn(null)
	}, [pollGlobalNotifications])

	useEffect(() => {
		if (!prioritySummaryStore.fetchedOnce) {
			pollGlobalNotifications()
		}
	}, [pollGlobalNotifications])

	useEffect(() => {
		const segments = pathname.split('/').filter(Boolean)
		const pagePath = segments[0]

		if (pagePath === 'student_profile') {
			setCurrentPage('')
			return
		}

		if (pagePath === 'applications' && segments.length > 1) {
			setCurrentPage('')
			return
		}

		const title = pagePath || ''

		setCurrentPage(title.toUpperCase())
	}, [pathname])

	const setHeaderActions = useCallback(node => {
		setHeaderActionsState(() => node || null)
	}, [])

	const setHeaderLeading = useCallback(node => {
		setHeaderLeadingState(() => node || null)
	}, [])

	return {
		currentPage,
		headerActions,
		headerLeading,
		setHeaderActions,
		setHeaderLeading,
		isApplicationDetailsPage,
		isStudentProfilePage,
		isMessagesPage,
	}
}
